import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@client/components/hooks/use-toast';
import { selectSessionAdvisor, selectAdvisorHierarchyState } from '@client/model/selectors';
import {
  deleteAdvisorHierarchyRelationshipByAdvisorIdRequest,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
} from '@client/model/advisorHierarchyModel';

interface UseAdvisorHierarchyLeaveOptions {
  /** Whether admin mode is active - disables leave functionality when true */
  adminModeActive?: boolean;
}

export function useAdvisorHierarchyLeave({
  adminModeActive = false,
}: UseAdvisorHierarchyLeaveOptions = {}) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const { deleteMutationStatus, deleteMutationError } = useSelector(selectAdvisorHierarchyState);

  const advisorId = sessionAdvisor?.advisorId;

  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [relationshipToLeave, setRelationshipToLeave] =
    useState<SupervisorAdvisorRelationship | null>(null);
  const prevLeavingRef = useRef(false);
  // Track whether this hook initiated the leave operation
  const initiatedLeaveRef = useRef(false);

  // Handle leave mutation completion
  useEffect(() => {
    const isLeaving = deleteMutationStatus === 'loading';

    // Mutation just completed (was leaving, now not) and this hook initiated it
    if (prevLeavingRef.current && !isLeaving && initiatedLeaveRef.current) {
      initiatedLeaveRef.current = false;
      if (deleteMutationError) {
        toast({
          title: 'Error',
          description: deleteMutationError || 'Failed to leave team. Please try again.',
          variant: 'destructive',
          className: '-translate-y-5',
        });
      } else if (deleteMutationStatus === 'success') {
        toast({
          title: 'Success',
          description: 'You have left the team successfully.',
          className: '-translate-y-5',
        });
      }
    }

    prevLeavingRef.current = isLeaving;
  }, [deleteMutationStatus, deleteMutationError, dispatch, sessionAdvisor?.advisorId, toast]);

  const handleLeave = (relationship: SupervisorAdvisorRelationship) => {
    setRelationshipToLeave(relationship);
    setLeaveDialogOpen(true);
  };

  const confirmLeave = () => {
    // Block action in admin mode
    if (adminModeActive) {
      toast({
        title: 'Action Not Allowed',
        description: 'Cannot leave a team on behalf of another advisor.',
        variant: 'destructive',
        className: '-translate-y-5',
      });
      setLeaveDialogOpen(false);
      setRelationshipToLeave(null);
      return;
    }

    if (!relationshipToLeave || !advisorId) {
      return;
    }

    if (!relationshipToLeave.advisorId) {
      toast({
        title: 'Error',
        description: 'Unable to leave team. Missing supervisor information.',
        variant: 'destructive',
        className: '-translate-y-5',
      });
      return;
    }

    initiatedLeaveRef.current = true;
    dispatch(
      deleteAdvisorHierarchyRelationshipByAdvisorIdRequest({
        advisorId,
        supervisorAdvisorId: relationshipToLeave.advisorId,
      }),
    );

    setLeaveDialogOpen(false);
    setRelationshipToLeave(null);
  };

  const cancelLeave = () => {
    setLeaveDialogOpen(false);
    setRelationshipToLeave(null);
  };

  return {
    // Dialog state
    leaveDialogOpen,
    relationshipToLeave,

    // Mutation state
    isLeaving: deleteMutationStatus === 'loading',

    // Admin mode state
    isDisabled: adminModeActive,

    // Actions
    handleLeave,
    confirmLeave,
    cancelLeave,
  };
}

export type UseAdvisorHierarchyLeaveReturn = ReturnType<typeof useAdvisorHierarchyLeave>;

